import { ContributionRecord, SummaryMetric } from '../pages/reports/types';

type TableCell = string | number | boolean | null | undefined;

let xlsxModulePromise: Promise<typeof import('xlsx')> | null = null;
let pdfModulesPromise: Promise<{
  jsPDF: (typeof import('jspdf'))['default'];
  autoTable: (typeof import('jspdf-autotable'))['default'];
}> | null = null;

const loadXLSX = async () => {
  if (!xlsxModulePromise) {
    xlsxModulePromise = import('xlsx');
  }
  return xlsxModulePromise;
};

const loadPDFModules = async () => {
  if (!pdfModulesPromise) {
    pdfModulesPromise = Promise.all([import('jspdf'), import('jspdf-autotable')]).then(
      ([jspdfModule, autoTableModule]) => ({
        jsPDF: jspdfModule.default,
        autoTable: autoTableModule.default,
      })
    );
  }
  return pdfModulesPromise;
};

export type ExportTableColumn = {
  key: string;
  label: string;
};

export type ExportTableOptions = {
  title: string;
  subtitle?: string;
  filename: string; // without extension
  columns: ExportTableColumn[];
  rows: Array<Record<string, TableCell>>;
  logoUrl?: string;
};

// Format currency with VUV
export const formatCurrency = (amount: number): string => {
  return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VUV`;
};

const formatCell = (value: TableCell): string => {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

export const exportTableToCSV = (opts: ExportTableOptions) => {
  const headers = opts.columns.map(c => c.label);
  const rows = opts.rows.map(r => opts.columns.map(c => formatCell(r[c.key])));

  const csvContent = [
    [opts.title],
    ...(opts.subtitle ? [[opts.subtitle]] : []),
    [],
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${opts.filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportTableToExcel = async (opts: ExportTableOptions) => {
  const XLSX = await loadXLSX();
  const workbook = XLSX.utils.book_new();
  const headers = opts.columns.map(c => c.label);
  const rows = opts.rows.map(r => opts.columns.map(c => formatCell(r[c.key])));

  const headerRows: any[] = [
    [opts.title],
    ...(opts.subtitle ? [[opts.subtitle]] : []),
  ];

  // Add logo note if available (Excel doesn't easily support embedded images)
  if (opts.logoUrl) {
    headerRows.push([`Logo: ${opts.logoUrl}`]);
  }

  headerRows.push([]); // Empty row before headers
  headerRows.push(headers);
  headerRows.push(...rows);

  const sheet = XLSX.utils.aoa_to_sheet(headerRows);

  sheet['!cols'] = opts.columns.map(() => ({ wch: 22 }));

  XLSX.utils.book_append_sheet(workbook, sheet, 'Report');
  XLSX.writeFile(workbook, `${opts.filename}.xlsx`);
};

export const exportTableToPDF = async (opts: ExportTableOptions) => {
  const { jsPDF, autoTable } = await loadPDFModules();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let startY = 18;

  // Add logo if provided
  if (opts.logoUrl) {
    try {
      let imageUrl = opts.logoUrl;
      if (!opts.logoUrl.startsWith('http')) {
        if (opts.logoUrl.startsWith('/')) {
          imageUrl = `${window.location.origin}${opts.logoUrl}`;
        } else {
          imageUrl = `${window.location.origin}/${opts.logoUrl}`;
        }
      }

      console.log('[PDF Export Table] Loading logo from:', imageUrl);

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[PDF Export Table] Logo load timeout');
          resolve(null);
        }, 10000);

        img.onload = () => {
          clearTimeout(timeout);
          try {
            const maxWidth = 30;
            const aspectRatio = img.height / img.width;
            const logoWidth = Math.min(maxWidth, img.width * 0.264583);
            const logoHeight = logoWidth * aspectRatio;

            doc.addImage(img, 'PNG', pageWidth - margin - logoWidth, margin, logoWidth, logoHeight);
            startY = Math.max(startY, margin + logoHeight + 5);
            console.log('[PDF Export Table] Logo added successfully');
            resolve(null);
          } catch (err) {
            console.warn('[PDF Export Table] Error adding logo to PDF:', err);
            resolve(null);
          }
        };
        img.onerror = (err) => {
          clearTimeout(timeout);
          console.warn('[PDF Export Table] Error loading logo image:', imageUrl, err);
          resolve(null);
        };
        img.src = imageUrl;
      });
    } catch (err) {
      console.warn('[PDF Export Table] Error processing logo for PDF:', err);
    }
  } else {
  }

  doc.setFontSize(16);
  doc.text(opts.title, margin, startY);
  startY += 8;

  if (opts.subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    doc.text(opts.subtitle, margin, startY);
    doc.setTextColor(0, 0, 0);
    startY += 6;
  }

  const tableData = opts.rows.map(r => opts.columns.map(c => formatCell(r[c.key])));
  autoTable(doc, {
    startY: startY,
    head: [opts.columns.map(c => c.label)],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [66, 139, 202] },
    styles: { fontSize: 8 },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Generated on ${new Date().toLocaleString()}`, margin, doc.internal.pageSize.getHeight() - 10);
    },
  });

  doc.save(`${opts.filename}.pdf`);
};

// Export to CSV
export const exportToCSV = (
  records: ContributionRecord[],
  summaryMetrics: SummaryMetric[],
  reportType: string,
  dateRange: { startDate: string; endDate: string },
  groupNames?: string[],
  logoUrl?: string
) => {
  const headers = ['Date', 'Group Name', 'Member Name', 'Amount (VUV)', 'Recorded By', 'Status', 'Notes'];
  const rows = records.map(record => [
    record.date,
    record.groupName || 'Unknown Group',
    record.memberName,
    formatCurrency(record.amount),
    record.recordedBy,
    record.verificationStatus,
    record.notes || ''
  ]);

  // Get unique group names from records if not provided
  const uniqueGroupNames = groupNames || [...new Set(records.map(r => r.groupName).filter(Boolean))];
  const groupNameHeader = uniqueGroupNames.length > 0
    ? `Group(s): ${uniqueGroupNames.join(', ')}`
    : 'Group: All Groups';

  // Add summary section
  const summaryRows = [
    [],
    ['Summary'],
    ...summaryMetrics.map(metric => [metric.label, metric.value])
  ];

  const csvContent = [
    [`${reportType.toUpperCase()} Report`],
    [groupNameHeader],
    [`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`],
    [],
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ...summaryRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `report_${reportType}_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Export to Excel
export const exportToExcel = async (
  records: ContributionRecord[],
  summaryMetrics: SummaryMetric[],
  reportType: string,
  dateRange: { startDate: string; endDate: string },
  groupNames?: string[],
  logoUrl?: string
) => {
  const XLSX = await loadXLSX();
  const workbook = XLSX.utils.book_new();

  // Get unique group names from records if not provided
  const uniqueGroupNames = groupNames || [...new Set(records.map(r => r.groupName).filter(Boolean))];
  const groupNameHeader = uniqueGroupNames.length > 0
    ? `Group(s): ${uniqueGroupNames.join(', ')}`
    : 'Group: All Groups';

  // Create data sheet
  const dataHeaders = ['Date', 'Group Name', 'Member Name', 'Amount (VUV)', 'Recorded By', 'Status', 'Notes'];
  const dataRows = records.map(record => [
    record.date,
    record.groupName || 'Unknown Group',
    record.memberName,
    formatCurrency(record.amount), // Format with VUV
    record.recordedBy,
    record.verificationStatus,
    record.notes || ''
  ]);

  // Build header rows with logo note if available
  const headerRows: any[] = [
    [`${reportType.toUpperCase()} Report`],
    [groupNameHeader],
    [`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`],
  ];

  // Add logo note if available (Excel doesn't easily support embedded images)
  if (logoUrl) {
    headerRows.push([`Logo: ${logoUrl}`]);
  }

  headerRows.push([]); // Empty row before headers
  headerRows.push(dataHeaders);
  headerRows.push(...dataRows);

  const dataSheet = XLSX.utils.aoa_to_sheet(headerRows);

  // Set column widths
  dataSheet['!cols'] = [
    { wch: 12 }, // Date
    { wch: 20 }, // Group Name
    { wch: 20 }, // Member Name
    { wch: 15 }, // Amount
    { wch: 18 }, // Recorded By
    { wch: 12 }, // Status
    { wch: 30 }  // Notes
  ];

  XLSX.utils.book_append_sheet(workbook, dataSheet, 'Contributions');

  // Create summary sheet
  const summaryHeaders = ['Metric', 'Value'];
  const summaryRows = summaryMetrics.map(metric => [metric.label, metric.value]);
  const summarySheet = XLSX.utils.aoa_to_sheet([
    ['Summary'],
    [],
    summaryHeaders,
    ...summaryRows
  ]);

  summarySheet['!cols'] = [
    { wch: 25 }, // Metric
    { wch: 20 }  // Value
  ];

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Generate filename and save
  const filename = `report_${reportType}_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;
  XLSX.writeFile(workbook, filename);
};

// Export to PDF
export const exportToPDF = async (
  records: ContributionRecord[],
  summaryMetrics: SummaryMetric[],
  reportType: string,
  dateRange: { startDate: string; endDate: string },
  groupNames?: string[],
  logoUrl?: string
) => {
  const { jsPDF, autoTable } = await loadPDFModules();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let startY = 20;

  // Get unique group names from records if not provided
  const uniqueGroupNames = groupNames || [...new Set(records.map(r => r.groupName).filter(Boolean))];
  const groupNameText = uniqueGroupNames.length > 0
    ? `Group(s): ${uniqueGroupNames.join(', ')}`
    : 'Group: All Groups';

  // Add logo if provided
  if (logoUrl) {
    try {
      // Convert logo URL to image data
      // If it's a relative path, convert to absolute
      let imageUrl = logoUrl;
      if (!logoUrl.startsWith('http')) {
        if (logoUrl.startsWith('/')) {
          imageUrl = `${window.location.origin}${logoUrl}`;
        } else {
          imageUrl = `${window.location.origin}/${logoUrl}`;
        }
      }

      console.log('[PDF Export] Loading logo from:', imageUrl);

      // Load image
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[PDF Export] Logo load timeout');
          resolve(null);
        }, 10000); // 10 second timeout

        img.onload = () => {
          clearTimeout(timeout);
          try {
            // Calculate logo dimensions (max 30mm width, maintain aspect ratio)
            const maxWidth = 30;
            const aspectRatio = img.height / img.width;
            const logoWidth = Math.min(maxWidth, img.width * 0.264583); // Convert px to mm
            const logoHeight = logoWidth * aspectRatio;

            // Add logo at top right
            doc.addImage(img, 'PNG', pageWidth - margin - logoWidth, margin, logoWidth, logoHeight);
            startY = Math.max(startY, margin + logoHeight + 5);
            console.log('[PDF Export] Logo added successfully');
            resolve(null);
          } catch (err) {
            console.warn('[PDF Export] Error adding logo to PDF:', err);
            resolve(null); // Continue without logo
          }
        };
        img.onerror = (err) => {
          clearTimeout(timeout);
          console.warn('[PDF Export] Error loading logo image:', imageUrl, err);
          resolve(null); // Continue without logo
        };
        img.src = imageUrl;
      });
    } catch (err) {
      console.warn('[PDF Export] Error processing logo for PDF:', err);
      // Continue without logo
    }
  }

  // Title
  doc.setFontSize(18);
  doc.text(`${reportType.toUpperCase()} Report`, margin, startY);
  startY += 8;

  // Group name(s)
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(groupNameText, margin, startY);
  startY += 7;

  // Date range
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`, margin, startY);
  startY += 10;

  // Summary section
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Summary', margin, startY);
  startY += 5;

  const summaryData = summaryMetrics.map(metric => [metric.label, metric.value]);
  autoTable(doc, {
    startY: startY,
    head: [['Metric', 'Value']],
    body: summaryData,
    theme: 'striped',
    headStyles: { fillColor: [66, 139, 202] },
    margin: { left: margin, right: margin }
  });

  // Contributions table
  const tableStartY = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 20 : startY + 20;
  doc.setFontSize(12);
  doc.text('Contributions', margin, tableStartY - 10);

  const tableData = records.map(record => [
    record.date,
    record.groupName || 'Unknown Group',
    record.memberName,
    formatCurrency(record.amount),
    record.recordedBy,
    record.verificationStatus,
    record.notes || ''
  ]);

  autoTable(doc, {
    startY: tableStartY,
    head: [['Date', 'Group Name', 'Member Name', 'Amount (VUV)', 'Recorded By', 'Status', 'Notes']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [66, 139, 202] },
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 20 }, // Date
      1: { cellWidth: 30 }, // Group Name
      2: { cellWidth: 30 }, // Member Name
      3: { cellWidth: 25 }, // Amount
      4: { cellWidth: 25 }, // Recorded By
      5: { cellWidth: 20 }, // Status
      6: { cellWidth: 35 }  // Notes
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data: any) => {
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} - communitySaving`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
  });

  // Save PDF
  const filename = `report_${reportType}_${dateRange.startDate}_to_${dateRange.endDate}.pdf`;
  doc.save(filename);
};

